import { useState } from "react";
import { REWORK_TYPES, REWORK_CAUSES, getCausesByCategory } from '../config/reworkConstants';

/**
 * Modal for capturing the WHAT and CAUSE of REWORK or REVISION operations.
 * Appears before starting any REWORK or REVISION operation.
 *
 * Collects:
 * - reworkType: WHAT kind of rework (reprint_only, reprint_opti, etc.)
 * - cause: WHY the rework is needed (pa_error, headstrike, etc.)
 * - notes: Additional context
 */

export default function ReworkCauseModal({
  operationType = 'REWORK', // 'REWORK' or 'REVISION'
  job,
  jobs = [],
  isMulti = false,
  onSubmit,
  onCancel
}) {
  const [reworkType, setReworkType] = useState('');
  const [cause, setCause] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    if (!reworkType || !cause) return;
    onSubmit({
      reworkType,
      cause,
      notes: notes.trim() || null
    });
  };

  const isRevision = operationType === 'REVISION';
  const displayJobs = isMulti ? jobs : (job ? [job] : []);
  const jobCount = displayJobs.length;
  const causesByCategory = getCausesByCategory();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 border-b border-gray-200 ${isRevision ? 'bg-amber-50' : 'bg-red-50'}`}>
          <h2 className="text-lg font-semibold text-gray-900">
            {isRevision ? 'Revision Details' : 'Rework Details'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Tell us what needs to be redone and why
          </p>
        </div>

        {/* Job Info */}
        <div className="px-6 py-3 border-b border-gray-200 bg-slate-100">
          {jobCount === 1 ? (
            <p className="text-sm text-gray-700">
              <span className="text-gray-500">Job:</span>{" "}
              <span className="font-medium">{displayJobs[0]?.jobNumber || displayJobs[0]?.name || "Unknown"}</span>
              {displayJobs[0]?.partNumber && (
                <span className="text-gray-500 ml-2">({displayJobs[0].partNumber})</span>
              )}
            </p>
          ) : (
            <p className="text-sm text-gray-700">
              <span className="text-gray-500">Starting {jobCount} jobs</span>
            </p>
          )}
        </div>

        {/* Form Content */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          {/* WHAT - Rework Type (Required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What needs to be redone? <span className="text-red-500">*</span>
            </label>
            <select
              value={reworkType}
              onChange={(e) => setReworkType(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            >
              <option value="">Select type of rework...</option>
              {REWORK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {reworkType && (
              <p className="text-xs text-gray-500 mt-1">
                {REWORK_TYPES.find(t => t.value === reworkType)?.description}
              </p>
            )}
          </div>

          {/* CAUSE - Root Cause (Required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What caused this {isRevision ? 'revision' : 'rework'}? <span className="text-red-500">*</span>
            </label>
            <select
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
            >
              <option value="">Select a cause...</option>
              {Object.entries(causesByCategory).map(([categoryKey, category]) => (
                <optgroup key={categoryKey} label={category.label}>
                  {category.causes.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {cause && (
              <p className="text-xs text-gray-500 mt-1">
                {REWORK_CAUSES.find(c => c.value === cause)?.description}
              </p>
            )}
          </div>

          {/* Notes (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Briefly describe what went wrong..."
              rows={3}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reworkType || !cause}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${reworkType && cause
                ? isRevision
                  ? "bg-amber-500 hover:bg-amber-600 text-white"
                  : "bg-red-500 hover:bg-red-600 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }
            `}
          >
            {isMulti ? `Start ${jobCount} Jobs` : 'Start Job'}
          </button>
        </div>
      </div>
    </div>
  );
}
